import React from "react";
import ReactEcharts from "echarts-for-react";

const mappingMonth: any = {
  มกราคม: "ม.ค.",
  กุมภาพันธ์: "ก.พ.",
  มีนาคม: "มี.ค.",
  เมษายน: "เม.ย.",
  พฤษภาคม: "พ.ค.",
  มิถุนายน: "มิ.ย.",
  กรกฎาคม: "ก.ค.",
  สิงหาคม: "ส.ค.",
  กันยายน: "ก.ย.",
  ตุลาคม: "ต.ค.",
  พฤศจิกายน: "พ.ย.",
  ธันวาคม: "ธ.ค.",
};

const mappingMonth2: any = {
  January: "ม.ค.",
  February: "ก.พ.",
  March: "มี.ค.",
  April: "เม.ย.",
  May: "พ.ค.",
  June: "มิ.ย.",
  July: "ก.ค.",
  August: "ส.ค.",
  September: "ก.ย.",
  October: "ต.ค.",
  November: "พ.ย.",
  December: "ธ.ค.",
};

interface StackedBarNormalizationProps {
  data: {
    [key: string]: {
      data: { key: string; value: number; label: string }[];
      color: string;
    };
  };
  title: string;
  yAxisName: string;
  height: string;
  baseSeriesConfig: {
    name: string;
    color: string;
  };
  overlaySeriesNames: string[];
}

export const StackedBarNormalization: React.FC<StackedBarNormalizationProps> = ({ 
  data, 
  title, 
  yAxisName, 
  height, 
  baseSeriesConfig,
  overlaySeriesNames
}) => {
  
  const options = {
    title: {
      text: title,
    },
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
      formatter: (params: any) => {
        let result = `${params[0].axisValue}<br/>`;
        params.forEach((param: any) => {
          if (overlaySeriesNames.includes(param.seriesName)) {
            const originalValue = data[param.seriesName].data[param.dataIndex].value;
            const percentage = Math.round(originalValue * 100);
            result += `${param.marker} ${param.seriesName}: ${percentage}%<br/>`;
          } else {
            result += `${param.marker} ${param.seriesName}: ${Math.round(param.value).toLocaleString()}<br/>`;
          }
        });
        return result;
      },
    },
    legend: {
      data: Object.keys(data).filter(key => key !== baseSeriesConfig.name),
      bottom: "5%",
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "15%",
      containLabel: true,
    },
    xAxis: [
      {
        type: "category",
        data: data[Object.keys(data)[0]].data.map(
          (item) => {

            const monthAbbr = mappingMonth[item.label] || mappingMonth2[item.label];
            
            // Try to extract year from different formats
            let year = new Date().getFullYear() + 543; // Default to current year in Buddhist Era
            
            if (item.key) {
              // Try parsing as date
              const date = new Date(item.key);
              if (!isNaN(date.getTime())) {
                year = date.getFullYear() + 543;
              } else {
                // Try extracting year from string (e.g., "2024-01", "January 2024")
                const yearMatch = item.key.match(/\d{4}/);
                if (yearMatch) {
                  year = parseInt(yearMatch[0]) + 543;
                }
              }
            }
            
            return `${monthAbbr} ${year}`;
          }
        ),
        splitLine: {
          show: true,
          lineStyle: {
            color: "#e0e0e0",
            type: "solid",
          },
        },
      },
    ],
    yAxis: [
      {
        type: "value",
        name: yAxisName,
        splitLine: {
          show: true,
          lineStyle: {
            color: "#e0e0e0",
            type: "solid",
          },
        },
      },
    ],
    series: [
      // Base bar
      {
        name: baseSeriesConfig.name,
        type: "bar",
        data: data[baseSeriesConfig.name]?.data.map(item => item.value) || [],
        itemStyle: {
          color: baseSeriesConfig.color,
          borderRadius: [8, 8, 0, 0],
        },
        label: {
          show: true,
          position: "top",
          formatter: (params: any) => {
            return Math.round(params.value).toLocaleString();
          },
          color: "#333",
          fontSize: 12,
        },
        z: 1,
      },
      // Overlay bars
      ...Object.keys(data)
        .filter(key => overlaySeriesNames.includes(key))
        .map((key, index) => ({
          name: key,
          type: "bar",
          stack: "personnel",
          barGap: "-100%", // Overlay on the same position
          data: data[key].data.map((item, dataIndex) => {
            const totalCarbon = data[baseSeriesConfig.name]?.data[dataIndex]?.value || 0;
            return item.value * totalCarbon;
          }),
          itemStyle: {
            color: data[key].color,
            borderRadius: index === 1 ? [8, 8, 0, 0] : [0, 0, 0, 0],
          },
          emphasis: {
            focus: "series",
          },
          label: {
            show: true,
            color: "#ffffff",
            formatter: (params: any) => {
              const totalCarbon = data[baseSeriesConfig.name]?.data[params.dataIndex]?.value || 0;
              const originalValue = data[key].data[params.dataIndex].value;
              const percentage = Math.round(originalValue * 100);
              
              // Hide % if carbon is 0
              if (totalCarbon === 0) return "";
              
              return percentage > 0 ? `${percentage}%` : "";
            },
          },
          z: 2,
        })),
    ],
  };

  return (
    <ReactEcharts option={options} style={{ height: height, width: "100%" }} />
  );
};
